/**
 * Language + Region picker chip. Two variants:
 *   variant="chip"     — compact pill for the login header / app header.
 *   variant="menuitem" — full-width row for dropdown menus.
 */
import { useEffect, useRef, useState } from "react";
import { Globe2, Check, ChevronDown } from "lucide-react";
import { LANGUAGES, REGIONS, useLocale } from "@/context/LocaleContext";

export default function LanguageRegionPicker({ variant = "chip", align = "right" }) {
  const {
    language, setLanguage, languageLabel, languageNative,
    region, setRegion, regionLabel,
  } = useLocale();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const trigger = variant === "chip" ? (
    <button
      type="button"
      onClick={() => setOpen((v) => !v)}
      className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[11px] tracking-[0.06em] uppercase text-[#5A5C60] hover:text-[#0A1A2F] hover:bg-[#FAFAF7] border border-transparent hover:border-[#E7E7E4] transition-colors"
      data-testid="lang-region-chip"
      aria-haspopup="listbox"
      aria-expanded={open}>
      <Globe2 className="h-3.5 w-3.5" strokeWidth={1.5} />
      <span>{language.toUpperCase()}</span>
      <span aria-hidden className="text-[#C7C7C4]">·</span>
      <span>{regionLabel}</span>
      <ChevronDown className={"h-3 w-3 text-[#8A8B8D] transition-transform " + (open ? "rotate-180" : "")} strokeWidth={1.6} />
    </button>
  ) : (
    <button
      type="button"
      onClick={() => setOpen((v) => !v)}
      className="w-full flex items-center gap-2 hover:bg-slate-50 rounded-md p-1.5 text-sm text-slate-700"
      data-testid="lang-region-menuitem">
      <Globe2 className="h-4 w-4 text-slate-500" />
      <span className="flex-1 text-left">Language &amp; region</span>
      <span className="text-xs text-slate-500">{language.toUpperCase()} · {regionLabel}</span>
    </button>
  );

  const panelPos = align === "right" ? "right-0" : "left-0";

  return (
    <div className="relative" ref={ref}>
      {trigger}
      {open && (
        <div
          className={"absolute " + panelPos + " top-full mt-2 z-50 w-[420px] max-w-[92vw] rounded-lg border border-[#E7E7E4] bg-white shadow-[0_18px_48px_-24px_rgba(10,26,47,0.20)]"}
          role="dialog"
          aria-label="Language and region"
          data-testid="lang-region-panel">
          <div className="p-4 border-b border-[#E7E7E4]">
            <div className="text-[10px] tracking-[0.18em] uppercase text-[#8A8B8D] font-medium">Language &amp; region</div>
            <p className="mt-1 text-[12px] text-[#5A5C60]">Applies to dates, numbers, currency and platform text where available.</p>
          </div>
          <div className="grid grid-cols-2 divide-x divide-[#E7E7E4]">
            <div className="p-3">
              <div className="text-[10px] tracking-[0.16em] uppercase text-[#8A8B8D] font-medium mb-2 px-1">Language</div>
              <ul className="max-h-[280px] overflow-y-auto space-y-0.5" role="listbox" aria-label="Language">
                {LANGUAGES.map((l) => (
                  <li key={l.code}>
                    <button
                      type="button"
                      onClick={() => setLanguage(l.code)}
                      className={
                        "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[13px] text-left transition-colors " +
                        (language === l.code ? "bg-[#FAFAF7] text-[#0A1A2F]" : "text-[#3B3D40] hover:bg-[#FAFAF7]")
                      }
                      role="option"
                      aria-selected={language === l.code}
                      data-testid={`lang-opt-${l.code}`}>
                      <span aria-hidden className="text-sm">{l.flag}</span>
                      <span className="flex-1 truncate">
                        <span className="text-[13px]">{l.native}</span>
                        <span className="text-[10px] text-[#8A8B8D] ml-1.5">{l.label}</span>
                      </span>
                      {language === l.code && <Check className="h-3.5 w-3.5 text-[#0A1A2F]" strokeWidth={2} />}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-3">
              <div className="text-[10px] tracking-[0.16em] uppercase text-[#8A8B8D] font-medium mb-2 px-1">Region</div>
              <ul className="max-h-[280px] overflow-y-auto space-y-0.5" role="listbox" aria-label="Region">
                {REGIONS.map((r) => (
                  <li key={r.code}>
                    <button
                      type="button"
                      onClick={() => setRegion(r.code)}
                      className={
                        "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-[13px] text-left transition-colors " +
                        (region === r.code ? "bg-[#FAFAF7] text-[#0A1A2F]" : "text-[#3B3D40] hover:bg-[#FAFAF7]")
                      }
                      role="option"
                      aria-selected={region === r.code}
                      data-testid={`region-opt-${r.code}`}>
                      <span className="flex-1 truncate">
                        <span className="text-[13px]">{r.label}</span>
                        <span className="text-[10px] text-[#8A8B8D] ml-1.5">{r.currency}</span>
                      </span>
                      {region === r.code && <Check className="h-3.5 w-3.5 text-[#0A1A2F]" strokeWidth={2} />}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="px-4 py-2.5 border-t border-[#E7E7E4] text-[11px] text-[#8A8B8D] flex items-center justify-between">
            <span>Preference stored on this device</span>
            <button type="button" onClick={() => setOpen(false)} className="text-[11px] text-[#0A1A2F] hover:underline" data-testid="lang-region-close">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}
